import Constants from 'expo-constants';
import {
  ApolloClient,
  ApolloProvider,
  from,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { Badge, ThemeBase } from "react-native-paper";
import { NetworkError } from "@apollo/client/errors";
import { onError } from "@apollo/client/link/error";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { StatusBar } from "expo-status-bar";
import { GraphQLError } from "graphql";
import jwtDecode from "jwt-decode";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MD3LightTheme as DefaultTheme,
  MD3DarkTheme as DarkTheme,
  Provider as PaperProvider,
  Snackbar,
} from "react-native-paper";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { User, UserContext } from "./context/UserContext";
import useCachedResources from "./hooks/useCachedResources";
import useColorScheme from "./hooks/useColorScheme";
import "./i18n";
import { logger } from "./services/logger";
import { RootNavigator } from "./navigation/RootNavigator";

export default function App() {
  const isLoadingComplete = useCachedResources();
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [clientErrors, setClientErrors] = useState<
    (GraphQLError | NetworkError)[]
  >([]);
  useEffect(() => {
    async function run() {
      const token = await AsyncStorage.getItem("token");
      if (token) {
        setToken(token);
      }
    }
    run();
  }, []);
  useEffect(() => {
    if (!token) {
      setUser(null);
      AsyncStorage.removeItem("token").catch((err) => {
        logger.error("could not remove token", err);
      });
      return;
    }
    const decoded = jwtDecode<{ given_name: string; picture: string }>(token);
    setUser({
      name: decoded.given_name,
      picture: decoded.picture,
    });
    AsyncStorage.setItem("token", token).catch((err) => {
      logger.error("could not set token", err);
    });
  }, [token]);
  const colorScheme = useColorScheme();

  const reactPaperTheme =
    colorScheme === "dark"
      ? {
          ...DarkTheme,
          colors: {
            ...DarkTheme.colors,
            background: "grey",
          },
        }
      : {
          ...DefaultTheme,
          colors: {
            ...DefaultTheme.colors,
            background: "lightgrey",
          },
        };

  const client = useMemo(() => {
    const linkChain = new HttpLink({
      uri: Constants.manifest?.extra?.API_URL ?? "https://api-gateway-2.vercel.app/graphql",
      headers: {
        authorization: "Bearer " + token,
      },
    });

    const errorLink = onError((err) => {
      console.error(err)
      const { graphQLErrors, networkError } = err;
      const errors: typeof clientErrors = [];
      if(graphQLErrors) {
        errors.push(...(graphQLErrors as any[]));
        (graphQLErrors as any[]).forEach((err) => {
          logger.error(err.message, err);
        });
      } else if (networkError) {
        errors.push(networkError);
        //@ts-ignore
        networkError.toto = 'bbjhbj'
        logger.error(networkError);
      }
      setClientErrors(errors);
    });

    return new ApolloClient({
      cache: new InMemoryCache(),
      link: from([errorLink, linkChain]),
    });
  }, [token]);

  if (!isLoadingComplete) {
    return null;
  } else {
    return (
      <SafeAreaProvider>
        <PaperProvider theme={reactPaperTheme}>
          <UserContext.Provider value={{ user, token, setToken }}>
            <ApolloProvider client={client}>
              <RootNavigator/>
              {clientErrors[0] ? (
                <Snackbar
                  visible={Boolean(clientErrors.length)}
                  onDismiss={() => {}}
                  action={{
                    label: t("Dismiss"),
                    onPress: () => {
                      setClientErrors(clientErrors.slice(1));
                    },
                  }}
                >
                  <Badge onPressOut={() => {}} onPressIn={() => {}}>
                    {clientErrors.length}
                  </Badge>
                  {clientErrors[0].message}
                </Snackbar>
              ) : null}
              <StatusBar />
            </ApolloProvider>
          </UserContext.Provider>
        </PaperProvider>
      </SafeAreaProvider>
    );
  }
}
